import Layout from '../../components/Layout';
import RoutineStep from '../../components/RoutineStep';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const routineSteps = [
  {
    stepNumber: 1,
    title: "Cleanse",
    description: "Start your day by removing any overnight buildup and preparing your skin to absorb the products that follow. A gentle cleanser won't strip your skin's natural moisture barrier.",
    products: [
      {
        name: "Gentle Foaming Cleanser",
        description: "A pH-balanced, non-stripping cleanser perfect for daily use.",
        bestFor: "All Skin Types",
        benefits: ["Removes impurities gently", "Maintains moisture barrier", "Fragrance-free formula"]
      },
      {
        name: "Cream Cleanser",
        description: "A hydrating option ideal for dry or sensitive skin types.",
        bestFor: "Dry & Sensitive Skin",
        benefits: ["Ultra-gentle formula", "Hydrates while cleansing", "No tight feeling after use"]
      }
    ]
  },
  {
    stepNumber: 2,
    title: "Tone (Optional)",
    description: "A toner can help balance your skin's pH and prep it for better absorption of serums and moisturizers. Skip if you have very sensitive skin or prefer a minimal routine.",
    products: [
      {
        name: "Hydrating Toner Essence",
        description: "A lightweight, alcohol-free toner that hydrates and preps.",
        bestFor: "All Skin Types",
        benefits: ["Alcohol-free", "Adds lightweight hydration", "Preps skin for serums"]
      }
    ]
  },
  {
    stepNumber: 3,
    title: "Serum (Vitamin C)",
    description: "Morning is the ideal time for antioxidant protection. Vitamin C helps defend against environmental damage and can brighten your complexion over time.",
    products: [
      {
        name: "Vitamin C Serum (15%)",
        description: "A stable, effective vitamin C formula for brightening and protection.",
        bestFor: "Most Skin Types",
        benefits: ["Brightens complexion", "Antioxidant protection", "Stable formula"]
      },
      {
        name: "Gentle Vitamin C Alternative",
        description: "A lower concentration option for sensitive or beginner users.",
        bestFor: "Sensitive & Beginner",
        benefits: ["Lower concentration (10%)", "Minimal irritation risk", "Good entry-level option"]
      }
    ]
  },
  {
    stepNumber: 4,
    title: "Moisturize",
    description: "Lock in hydration and create a protective barrier. Choose a texture that suits your skin type—lighter gels for oily skin, richer creams for dry skin.",
    products: [
      {
        name: "Lightweight Gel Moisturizer",
        description: "Fast-absorbing, non-greasy hydration for oily or combination skin.",
        bestFor: "Oily & Combination Skin",
        benefits: ["Oil-free formula", "Fast absorption", "No greasy residue"]
      },
      {
        name: "Nourishing Day Cream",
        description: "A richer formula for normal to dry skin types.",
        bestFor: "Normal & Dry Skin",
        benefits: ["Long-lasting hydration", "Nourishing ingredients", "Comfortable finish"]
      }
    ]
  },
  {
    stepNumber: 5,
    title: "Sunscreen (SPF 30+)",
    description: "The most important step in any morning routine. Sunscreen protects against premature aging, dark spots, and skin cancer. Apply generously and reapply throughout the day if exposed to sun.",
    products: [
      {
        name: "Mineral Sunscreen SPF 50",
        description: "A gentle, reef-safe mineral formula with no white cast.",
        bestFor: "Sensitive Skin",
        benefits: ["Mineral (zinc) formula", "Reef-safe", "No white cast"]
      },
      {
        name: "Lightweight Chemical SPF 50",
        description: "A cosmetically elegant formula that layers well under makeup.",
        bestFor: "All Skin Types",
        benefits: ["Lightweight texture", "No pilling under makeup", "Broad spectrum protection"]
      }
    ]
  }
];

const MorningRoutines = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="morning-routine-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/skincare" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Skincare
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Skincare Routine
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Morning Skincare Routine
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed mb-6">
            A simple, effective morning routine to protect your skin throughout the day. This guide covers the essential steps and products for all skin types.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-white text-[#374151] text-xs font-medium">Beginner Friendly</span>
            <span className="px-3 py-1 bg-white text-[#374151] text-xs font-medium">5 Steps</span>
            <span className="px-3 py-1 bg-white text-[#374151] text-xs font-medium">All Skin Types</span>
          </div>
        </div>
      </section>

      {/* Routine Steps */}
      <section data-testid="morning-routine-steps" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Your Morning Routine
          </h2>
          <p className="text-[#374151] mb-8">
            Follow these steps in order for best results. Each step builds on the previous one.
          </p>
          
          <div className="divide-y divide-[#E5E5E5]">
            {routineSteps.map((step) => (
              <RoutineStep key={step.stepNumber} {...step} />
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section data-testid="morning-routine-tips" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            Tips & Notes
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Frequency:</strong> Follow this routine every morning for best results. Consistency is key.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Wait times:</strong> Allow each product to absorb for 30–60 seconds before applying the next.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Sunscreen reapplication:</strong> If you're spending extended time outdoors, reapply sunscreen every 2 hours.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Start slow:</strong> If you're new to vitamin C, start using it every other day to let your skin adjust.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>

      {/* Next Guide CTA */}
      <section className="py-16 md:py-20 bg-[#F2EBE3]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs tracking-widest uppercase font-medium text-[#6B7280] mb-2">Next Guide</p>
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Evening Skincare Routine
          </h3>
          <p className="text-[#374151] mb-6">
            Complete your daily skincare with a nourishing evening routine focused on repair and renewal.
          </p>
          <Link 
            to="/skincare/evening-routines"
            data-testid="next-guide-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
          >
            View Evening Routine
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default MorningRoutines;
